import { Animation, Globe, SkyCoord, easing } from '@stellar-globe/stellar-globe'
import { useSyncExternalStore } from 'react'
import { useAppContext } from '../../context'
import { Bookmark, BookmarkFolder, BookmarkWalkConfig, childBookmarks } from './model'
import { useBookmarkState } from './storage'

function bookmarkCoordinates(bookmark: Bookmark) {
  return {
    coord: SkyCoord.fromRad(bookmark.view.a, bookmark.view.d),
    view: { fovy: bookmark.view.fovy, roll: bookmark.view.roll },
  }
}

type WalkController = ReturnType<typeof createWalkController>
const controllers = new WeakMap<Globe, WalkController>()

function createWalkController(globe: Globe) {
  let running = false
  let generation = 0
  let activeAnimation: Animation | undefined
  const listeners = new Set<() => void>()
  const notify = () => listeners.forEach(listener => listener())
  const setRunning = (next: boolean) => {
    if (running !== next) {
      running = next
      notify()
    }
  }
  const stop = () => {
    generation += 1
    activeAnimation?.stop()
    activeAnimation = undefined
    setRunning(false)
    // 停止時にビュー角度を勝手に変更しない。
  }
  const play = async (folder: BookmarkFolder, config: BookmarkWalkConfig) => {
    stop()
    const bookmarks = childBookmarks(folder)
    if (!bookmarks.length) return
    const token = ++generation
    const alive = () => generation === token
    const run = async (animation: Animation) => {
      activeAnimation = animation
      await animation
      if (activeAnimation === animation) activeAnimation = undefined
    }
    setRunning(true)
    try {
      await run(globe.animations.add(() => undefined, { duration: 1000 }))
      const functions = easing as unknown as Record<string, (r: number) => number>
      const easingFunction = functions[config.easing] ?? easing.slowStartStop4
      while (alive()) {
        for (const bookmark of bookmarks) {
          if (!alive()) return
          const { coord, view } = bookmarkCoordinates(bookmark)
          await run(globe.camera.jumpTo(view, {
            coord,
            duration: config.duration,
            keepFovy: config.keepFovy,
            easingFunction,
          }))
          if (!alive()) return
          await run(globe.animations.add(() => undefined, { duration: config.wait }))
        }
        if (!config.loop) break
      }
    } finally {
      if (alive()) {
        activeAnimation = undefined
        setRunning(false)
        globe.camera.jumpTo({ roll: 0 })
      }
    }
  }
  return {
    stop, play,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => { listeners.delete(listener) }
    },
    getSnapshot: () => running,
  }
}

function controllerFor(globe: Globe): WalkController {
  let controller = controllers.get(globe)
  if (!controller) {
    controller = createWalkController(globe)
    controllers.set(globe, controller)
  }
  return controller
}

export function useGotoBookmark() {
  const { globeHandle } = useAppContext()
  return (bookmark: Bookmark, duration = 4000) => {
    const globe = globeHandle.current?.()
    if (!globe) return
    controllerFor(globe).stop()
    const { coord, view } = bookmarkCoordinates(bookmark)
    globe.camera.jumpTo(view, { coord, duration, easingFunction: easing.slowStartStop4 })
  }
}

export function useBookmarkWalk() {
  const { globeHandle } = useAppContext()
  const { config } = useBookmarkState()
  // ref は初回 render 時に未設定の場合があるため、購読時・操作時に解決する。
  const resolve = () => {
    const globe = globeHandle.current?.()
    return globe ? controllerFor(globe) : undefined
  }
  const walking = useSyncExternalStore(
    listener => resolve()?.subscribe(listener) ?? emptySubscribe(),
    () => resolve()?.getSnapshot() ?? false,
    alwaysFalse,
  )
  return {
    walking,
    stop: () => resolve()?.stop(),
    walk: (folder: BookmarkFolder) => resolve()?.play(folder, config.walk),
  }
}

function emptySubscribe() {
  return () => undefined
}
function alwaysFalse() {
  return false
}
